import * as React from 'react';

import { enumUnit, Unit, Weight } from '@rematter/frontend-lib-api-client';
import { TouchSelect } from '@rematter/ui-web';
import {
  convertFromPounds,
  convertToPounds,
  shortenUnit,
} from '@rematter/ui-web/lib';

import { ItemFieldProps, NormalizedLineItem } from '../_types';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';

const unitOptions = Object.values(enumUnit).map((item) => ({
  label: shortenUnit(item),
  key: item,
}));

//
// ⚛️ ⚛️ ⚛️ PRICING UNIT FIELD ⚛️ ⚛️ ⚛️
//
export const PricingUnitField: React.FC<ItemFieldProps> = ({
  lineItem,
  disabled = false,
  onUpdateItem,
  onKeyDown,
}) => {
  // Translations
  const t = useTranslations();

  // Callbacks
  const handlePricingUnitChange = React.useCallback(
    (value: Unit | undefined) => {
      const newPricingUnit = value ?? enumUnit.Pounds;
      const oldPricingUnit = (lineItem.pricingUnit as Unit) ?? enumUnit.Pounds;

      const updates: Partial<NormalizedLineItem> = {
        pricingUnit: newPricingUnit,
      };

      if (oldPricingUnit !== newPricingUnit) {
        const convertWeight = (weight: Weight | null): Weight | null => {
          if (!weight) return null;

          const oldValue = weight.value;
          const oldUnit = weight.unit as Unit;

          const pounds = convertToPounds(oldValue, oldUnit);
          const newValue = convertFromPounds(pounds, newPricingUnit);

          return {
            ...weight,
            value: newValue,
            unit: newPricingUnit,
          };
        };

        updates.weights = {
          [enumLoadWeightType.Gross]: convertWeight(
            lineItem.weights?.[enumLoadWeightType.Gross] ?? null,
          ),
          [enumLoadWeightType.Tare]: convertWeight(
            lineItem.weights?.[enumLoadWeightType.Tare] ?? null,
          ),
          [enumLoadWeightType.Net]: convertWeight(
            lineItem.weights?.[enumLoadWeightType.Net] ?? null,
          ),
        };
      }

      onUpdateItem(lineItem.id, updates);
    },
    [lineItem, onUpdateItem],
  );

  return (
    <div onKeyDown={onKeyDown} className="@PricingUnitField">
      <TouchSelect
        aria-label={t.scaleTicketPro.columns.pricingUnit}
        options={unitOptions}
        value={(lineItem.pricingUnit as Unit) || enumUnit.Pounds}
        onChange={handlePricingUnitChange}
        isDisabled={disabled}
        className="@PricingUnitField"
      />
    </div>
  );
};
